import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, X, Download, ExternalLink, FileText, Image as ImageIcon, FileSpreadsheet, Tag, Calendar, User, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const formatBytes = (b) => {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const extOf = (name) => (name || '').split('.').pop()?.toLowerCase() || '';

const TypeIcon = ({ name }) => {
  const e = extOf(name);
  if (e === 'pdf') return <FileText className="w-6 h-6 text-red-500" />;
  if (['doc', 'docx'].includes(e)) return <FileText className="w-6 h-6 text-blue-500" />;
  if (['xls', 'xlsx', 'csv'].includes(e)) return <FileSpreadsheet className="w-6 h-6 text-green-500" />;
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(e)) return <ImageIcon className="w-6 h-6 text-purple-500" />;
  return <FileText className="w-6 h-6 text-[#8C8078]" />;
};

/**
 * Inline document preview pane. Renders PDFs in an iframe, images directly,
 * and text/CSV as monospace text. Anything else gets a "preview unavailable"
 * card with a Download button.
 */
export default function DocumentPreviewPane({ document: doc, clientId, token, onClose, onOpenFull }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [textPreview, setTextPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!doc || !token) return;
    let revoked = false;
    let currentUrl = null;

    setLoading(true);
    setError(null);
    setBlobUrl(null);
    setTextPreview(null);

    const ext = extOf(doc.name);
    const isText = ['txt', 'csv', 'log', 'md', 'json'].includes(ext);

    axios
      .get(`${API_BASE}/api/clients/${clientId}/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      })
      .then(async (res) => {
        if (revoked) return;
        if (isText) {
          const txt = await res.data.text();
          setTextPreview(txt.slice(0, 50_000));  // cap at 50KB for safety
        } else {
          currentUrl = URL.createObjectURL(res.data);
          setBlobUrl(currentUrl);
        }
      })
      .catch(() => setError('Preview failed to load'))
      .finally(() => { if (!revoked) setLoading(false); });

    return () => {
      revoked = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [doc, clientId, token]);

  if (!doc) return null;

  const ext = extOf(doc.name);
  const isPdf = ext === 'pdf';
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
  const isText = ['txt', 'csv', 'log', 'md', 'json'].includes(ext);
  const previewable = isPdf || isImage || isText;

  return (
    <aside
      data-testid="document-preview-pane"
      className={cn(
        // On xl, behaves as the 3rd column of the page grid (handled by parent)
        'bg-white border border-[#E6E0D9] rounded-2xl flex flex-col min-h-0',
        // On smaller screens, slides over as a fixed drawer
        'fixed inset-0 z-40 xl:static xl:z-0 xl:max-h-[calc(100vh-6rem)]'
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 p-4 border-b border-[#E6E0D9] shrink-0">
        <div className="flex items-start gap-3 min-w-0">
          <TypeIcon name={doc.name} />
          <div className="min-w-0">
            <p className="font-medium text-[#2C2420] truncate" title={doc.name}>{doc.name}</p>
            <p className="text-xs text-[#8C8078]">
              v{doc.version || 1} · {formatBytes(doc.size)} · {ext.toUpperCase() || 'FILE'}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={onClose}
          data-testid="preview-close-btn"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="aspect-[3/4] xl:aspect-auto xl:h-[420px] bg-[#F9F7F5] border-b border-[#E6E0D9] flex items-center justify-center">
          {loading && <Loader2 className="w-6 h-6 animate-spin text-[#4A3B32]" />}
          {!loading && error && <p className="text-sm text-red-600 px-4 text-center">{error}</p>}
          {!loading && !error && isPdf && blobUrl && (
            <iframe src={blobUrl} title={doc.name} className="w-full h-full" />
          )}
          {!loading && !error && isImage && blobUrl && (
            <img src={blobUrl} alt={doc.name} className="max-w-full max-h-full object-contain" />
          )}
          {!loading && !error && isText && textPreview !== null && (
            <pre className="w-full h-full overflow-auto p-3 text-xs font-mono text-[#2C2420] whitespace-pre-wrap break-words text-left">
              {textPreview}
            </pre>
          )}
          {!loading && !error && !previewable && (
            <div className="text-center px-4">
              <p className="text-sm text-[#5D524D] mb-2">Inline preview not available for .{ext}</p>
              <p className="text-xs text-[#8C8078]">Download to open in the appropriate app.</p>
            </div>
          )}
        </div>

        {/* Metadata */}
        <div className="p-4 space-y-3 text-sm">
          <div className="flex items-start gap-2">
            <Tag className="w-4 h-4 text-[#8C8078] mt-0.5 shrink-0" />
            <div>
              <p className="text-xs text-[#8C8078]">Category</p>
              <p className="text-[#2C2420]">{doc.category || '—'}</p>
            </div>
          </div>
          {doc.tags?.length > 0 && (
            <div className="flex items-start gap-2">
              <Tag className="w-4 h-4 text-[#8C8078] mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-[#8C8078] mb-1">Tags</p>
                <div className="flex flex-wrap gap-1">
                  {doc.tags.map((t) => (
                    <span key={t} className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{t}</span>
                  ))}
                </div>
              </div>
            </div>
          )}
          {doc.expiryDate && (
            <div className="flex items-start gap-2">
              <Clock className="w-4 h-4 text-[#8C8078] mt-0.5 shrink-0" />
              <div>
                <p className="text-xs text-[#8C8078]">Expires</p>
                <p className="text-[#2C2420]">{formatDate(doc.expiryDate)}</p>
              </div>
            </div>
          )}
          <div className="flex items-start gap-2">
            <User className="w-4 h-4 text-[#8C8078] mt-0.5 shrink-0" />
            <div>
              <p className="text-xs text-[#8C8078]">Uploaded</p>
              <p className="text-[#2C2420]">{doc.uploadedBy || '—'} · {formatDate(doc.uploadedAt)}</p>
            </div>
          </div>
          {doc.notes && (
            <div className="pt-2 border-t border-[#E6E0D9]">
              <p className="text-xs text-[#8C8078] mb-1">Notes</p>
              <p className="text-sm text-[#2C2420] whitespace-pre-wrap">{doc.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Footer actions */}
      <div className="p-3 border-t border-[#E6E0D9] flex items-center gap-2 shrink-0">
        {isPdf && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenFull?.(doc)}
            className="flex-1 gap-1"
            data-testid="preview-open-full-btn"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Open full
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={async () => {
            if (blobUrl) {
              const a = window.document.createElement('a');
              a.href = blobUrl;
              a.download = doc.name;
              a.click();
            } else {
              // Lazy: refetch
              const res = await axios.get(`${API_BASE}/api/clients/${clientId}/documents/${doc.id}/download`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
              });
              const url = URL.createObjectURL(res.data);
              const a = window.document.createElement('a');
              a.href = url;
              a.download = doc.name;
              a.click();
              URL.revokeObjectURL(url);
            }
          }}
          className="flex-1 gap-1"
          data-testid="preview-download-btn"
        >
          <Download className="w-3.5 h-3.5" />
          Download
        </Button>
      </div>
    </aside>
  );
}
